import React, { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Sidebar, { SidebarBackdrop } from "./Sidebar";
import GoBackButton from "../GoBackButton";
import { ROUTES } from "../../constants";
import { useNavigationOrigin } from "../../contexts";
import { selectSidebarOpen } from "../../store/selectors/uiSelectors";
import { setSidebarOpen } from "../../store/slices/uiSlice";
import { AppDispatch } from "../../store";
import {
  ContentLayout,
  LayoutContainer,
  GoBackContainer,
  MainContainer,
} from "./Layout.styled";

type LayoutProps = {
  children: React.ReactNode;
  topBarActions?: React.ReactNode;
};

const getEditPageParent = (pathname: string): string | undefined => {
  if (pathname.match(/^\/treatment-plan\/\d+\/edit$/)) {
    const id = pathname.match(/^\/treatment-plan\/(\d+)\/edit$/)?.[1];
    return id ? `/treatment-plan/${id}` : undefined;
  }

  if (pathname.match(/^\/user-management\/\d+\/edit$/)) {
    const id = pathname.match(/^\/user-management\/(\d+)\/edit$/)?.[1];
    return id ? `/user-management/${id}` : undefined;
  }

  if (pathname === "/profile/edit") {
    return "/profile";
  }

  return undefined;
};

const Layout: React.FC<LayoutProps> = ({ children, topBarActions }) => {
  const location = useLocation();
  const { getOrigin } = useNavigationOrigin();
  const dispatch = useDispatch<AppDispatch>();
  const isSidebarExpanded = useSelector(selectSidebarOpen);

  const shouldShowGoBack = location.pathname !== ROUTES.DASHBOARD;
  const shouldShowTopBar = shouldShowGoBack || Boolean(topBarActions);

  const parentRoute = useMemo(() => {
    const pathname = location.pathname;

    const editParent = getEditPageParent(pathname);
    if (editParent) {
      return editParent;
    }

    return getOrigin(pathname);
  }, [location.pathname, getOrigin]);

  const handleBackdropClick = () => {
    dispatch(setSidebarOpen(false));
  };

  return (
    <LayoutContainer>
      <Sidebar />
      <SidebarBackdrop expanded={isSidebarExpanded} onClick={handleBackdropClick} />
      <ContentLayout>
        <MainContainer>
          {shouldShowTopBar && (
            <GoBackContainer>
              {shouldShowGoBack ? (
                <GoBackButton to={parentRoute} />
              ) : (
                <div />
              )}
              {topBarActions}
            </GoBackContainer>
          )}
          {children}
        </MainContainer>
      </ContentLayout>
    </LayoutContainer>
  );
};

export default Layout;
